import { Component, Injector, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractComponent,
  Channel,
  ChannelEvent,
  ChannelHealth,
  ChannelHealthClassificationEnum,
  ChannelHealthPendingIssue,
  ChannelHealthService,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { translateEntityReason, translateIssueCategory, translateIssueTitle } from '../channel-health-i18n.util';

interface SignalCard {
  key: string;
  value: string;
  tone: 'ok' | 'warn' | 'bad' | 'muted';
}

/**
 * "Saúde do Canal" area (SPEC-2026-08-channel-health, T-22 — RF-22…RF-26, RNF-04).
 * Provider-state hero + "o que a Meta informa", pending-issue cards (external links only —
 * deliberately NO send-behavior control), per-entity diagnosis, signal cards, event history.
 */
@Component({
    selector: 'health-status-tab',
    templateUrl: './health-status-tab.component.html',
    styleUrl: './health-status-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HealthStatusTabComponent extends AbstractComponent implements OnChanges {

  @Input({ required: true }) channel: Channel;
  @Input() health?: ChannelHealth;
  @Input() pendingIssues: Array<ChannelHealthPendingIssue> = [];
  @Input() events: Array<ChannelEvent> = [];
  @Input() refreshTick: number = 0;

  extraEvents: Array<ChannelEvent> = [];
  eventsPage: number = 0;
  eventsExhausted: boolean = false;
  loadingEvents: boolean = false;

  private static readonly EVENTS_PAGE_SIZE = 50;

  classification = ChannelHealthClassificationEnum;

  constructor(
    public injector: Injector,
    private channelHealthService: ChannelHealthService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTick'] && !changes['refreshTick'].firstChange) {
      // The shell reloaded page 0 — drop the extra pages so the list doesn't duplicate.
      this.extraEvents = [];
      this.eventsPage = 0;
      this.eventsExhausted = false;
    }
  }

  get allEvents(): Array<ChannelEvent> {
    return [...(this.events ?? []), ...this.extraEvents];
  }

  get hasMoreEvents(): boolean {
    if (this.eventsExhausted) return false;
    return (this.events?.length ?? 0) >= HealthStatusTabComponent.EVENTS_PAGE_SIZE;
  }

  loadMoreEvents(): void {
    if (this.loadingEvents) return;
    this.loadingEvents = true;
    const nextPage = this.eventsPage + 1;
    this.channelHealthService.getHealthDetail(this.channel._id, nextPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.loadingEvents = false;
          this.eventsPage = nextPage;
          const page = detail.events ?? [];
          this.extraEvents = [...this.extraEvents, ...page];
          if (page.length < HealthStatusTabComponent.EVENTS_PAGE_SIZE) this.eventsExhausted = true;
        },
        error: (err) => {
          this.loadingEvents = false;
          this.alertService.error(err?.error?.message);
        },
      });
  }

  // ---------------------------------------------------------------- hero (RF-22)

  providerState(): string {
    return (this.health?.canSendMessage ?? '').toUpperCase();
  }

  heroTitle(): string {
    const map: Record<string, string> = {
      AVAILABLE: 'channel.health.status.heroAvailable',
      LIMITED: 'channel.health.status.heroLimited',
      BLOCKED: 'channel.health.status.heroBlocked',
    };
    return this.t(map[this.providerState()] ?? 'channel.health.status.heroNoData');
  }

  heroConsequence(): string {
    const map: Record<string, string> = {
      AVAILABLE: 'channel.health.status.heroAvailableText',
      LIMITED: 'channel.health.status.heroLimitedText',
      BLOCKED: 'channel.health.status.heroBlockedText',
    };
    return this.t(map[this.providerState()] ?? 'channel.health.status.heroNoDataText');
  }

  /** System Bootstrap alert variant for the provider-state hero. */
  heroAlertClass(): string {
    const map: Record<string, string> = {
      AVAILABLE: 'alert-success',
      LIMITED: 'alert-warning',
      BLOCKED: 'alert-danger',
    };
    return map[this.providerState()] ?? 'alert-secondary';
  }

  // ---------------------------------------------------------------- provider wording i18n (RNF-06)

  issueTitle(issue: ChannelHealthPendingIssue): string {
    return translateIssueTitle((key, params) => this.t(key, params), issue);
  }

  issueCategory(issue: ChannelHealthPendingIssue): string {
    return translateIssueCategory((key, params) => this.t(key, params), issue.category);
  }

  entityReason(entity: { reason?: string; reasonCode?: number }): string | undefined {
    return translateEntityReason((key, params) => this.t(key, params), entity as never);
  }

  // ---------------------------------------------------------------- entities (RF-25)

  entityLabel(type: string): string {
    const known = ['PHONE_NUMBER', 'WABA', 'BUSINESS', 'APP', 'MESSAGE_TEMPLATE'];
    return known.includes(type) ? this.t(`channel.health.entity.${type}`) : type;
  }

  entityStateLabel(state: string): string {
    const normalized = (state ?? '').toUpperCase();
    const known = ['AVAILABLE', 'LIMITED', 'BLOCKED'];
    return known.includes(normalized) ? this.t(`channel.health.state.${normalized}`) : (normalized || '—');
  }

  entityAlertClass(state: string): string {
    const normalized = (state ?? '').toUpperCase();
    if (normalized === 'AVAILABLE') return 'alert-success';
    if (normalized === 'LIMITED') return 'alert-warning';
    if (normalized === 'BLOCKED') return 'alert-danger';
    return 'alert-secondary';
  }

  // ---------------------------------------------------------------- signal cards (RF-26)

  signalCards(): Array<SignalCard> {
    const signals = this.health?.signals ?? {};
    const quality = (signals.qualityRating ?? '').toUpperCase();
    const cards: Array<SignalCard> = [
      {
        key: 'quality',
        value: quality === 'GREEN' ? this.t('channel.health.quality.high')
          : quality === 'YELLOW' ? this.t('channel.health.quality.medium')
          : quality === 'RED' ? this.t('channel.health.quality.low') : '—',
        tone: quality === 'GREEN' ? 'ok' : quality === 'YELLOW' ? 'warn' : quality === 'RED' ? 'bad' : 'muted',
      },
      {
        key: 'numberStatus',
        value: this.translateEnum('numberStatus', signals.numberStatus, ['CONNECTED', 'DISCONNECTED', 'FLAGGED', 'RESTRICTED', 'PENDING', 'OFFLINE', 'RATE_LIMITED']),
        tone: this.upper(signals.numberStatus) === 'CONNECTED' ? 'ok' : 'muted',
      },
      {
        key: 'nameStatus',
        // `name_status` describes the name IN USE; a requested new name lives in
        // `new_name_status` — while that review is open it is what the card must say.
        // AVAILABLE_WITHOUT_REVIEW on an unverified business is the state that caps the
        // messaging limit — the card says why (decision 2026-09-04).
        value: this.upper(signals.newNameStatus) === 'PENDING_REVIEW'
          ? this.t('channel.health.nameStatus.NEW_PENDING')
          : this.nameInUseWithoutReviewBlocked(signals)
            ? this.t('channel.health.nameStatus.AVAILABLE_WITHOUT_REVIEW_UNVERIFIED')
            : this.translateEnum('nameStatus', signals.nameStatus, ['APPROVED', 'PENDING_REVIEW', 'DECLINED', 'EXPIRED', 'AVAILABLE_WITHOUT_REVIEW', 'NONE']),
        tone: this.upper(signals.newNameStatus) === 'PENDING_REVIEW' || this.nameInUseWithoutReviewBlocked(signals) ? 'warn'
          : this.upper(signals.nameStatus) === 'APPROVED' ? 'ok'
          : this.upper(signals.nameStatus) === 'DECLINED' ? 'bad' : 'muted',
      },
      { key: 'messagingLimit', value: this.messagingLimitLabel(signals.messagingLimit), tone: 'muted' },
      {
        key: 'throughput',
        value: this.translateEnum('throughput', signals.throughputLevel, ['STANDARD', 'HIGH', 'NOT_APPLICABLE']),
        tone: 'muted',
      },
      {
        key: 'codeVerification',
        value: this.translateEnum('codeVerification', signals.codeVerificationStatus, ['VERIFIED', 'EXPIRED', 'NOT_VERIFIED', 'PENDING']),
        tone: this.upper(signals.codeVerificationStatus) === 'VERIFIED' ? 'ok' : 'muted',
      },
      {
        key: 'businessVerification',
        value: this.translateEnum('businessVerification', signals.businessVerificationStatus, ['VERIFIED', 'NOT_VERIFIED', 'PENDING', 'REJECTED', 'FAILED']),
        tone: this.upper(signals.businessVerificationStatus) === 'VERIFIED' ? 'ok' : this.upper(signals.businessVerificationStatus) === 'REJECTED' ? 'bad' : 'muted',
      },
      {
        key: 'accountReview',
        value: this.translateEnum('accountReview', signals.accountReviewStatus, ['APPROVED', 'PENDING', 'REJECTED']),
        tone: this.upper(signals.accountReviewStatus) === 'APPROVED' ? 'ok' : 'muted',
      },
      {
        key: 'oba',
        value: this.translateEnum('oba', signals.obaStatus, ['NOT_STARTED', 'IN_REVIEW', 'PENDING', 'APPROVED', 'REJECTED', 'DECLINED']),
        tone: this.upper(signals.obaStatus) === 'APPROVED' ? 'ok' : 'muted',
      },
      {
        key: 'billing',
        value: signals.billingBlocked === true
          ? this.t('channel.health.status.billingBlocked')
          : signals.billingBlocked === false ? this.t('channel.health.status.billingOk') : '—',
        tone: signals.billingBlocked === true ? 'bad' : signals.billingBlocked === false ? 'ok' : 'muted',
      },
    ];
    return cards;
  }

  /** Name in use without review while the business is unverified — the combination that caps the limit. */
  private nameInUseWithoutReviewBlocked(signals: { nameStatus?: string; businessVerificationStatus?: string }): boolean {
    return this.upper(signals.nameStatus) === 'AVAILABLE_WITHOUT_REVIEW'
      && this.upper(signals.businessVerificationStatus) !== 'VERIFIED';
  }

  /** Provider enum → translated label; unknown values fall back to the raw enum (RNF: never hide data). */
  private translateEnum(family: string, value: string | undefined, known: Array<string>): string {
    const normalized = (value ?? '').toUpperCase();
    if (!normalized) return '—';
    return known.includes(normalized) ? this.t(`channel.health.${family}.${normalized}`) : normalized;
  }

  /** Meta tier → conversations per 24h, in plain words (same mapping as the Resumo card). */
  private messagingLimitLabel(limit?: string): string {
    const tier = (limit ?? '').toUpperCase();
    const map: Record<string, string> = {
      TIER_50: '50',
      TIER_250: '250',
      TIER_1K: '1.000',
      TIER_10K: '10.000',
      TIER_100K: '100.000',
    };
    if (tier === 'TIER_UNLIMITED') return this.t('channel.health.summary.limitUnlimited');
    return map[tier] ? this.t('channel.health.summary.limitPerDay', { count: map[tier] }) : (tier || '—');
  }

  private upper(value?: string): string {
    return (value ?? '').toUpperCase() || '—';
  }


  // ---------------------------------------------------------------- events (RF-24)

  severityClass(event: ChannelEvent): string {
    const severity = (event.severity ?? '').toUpperCase();
    if (severity === 'CRITICAL') return 'event-dot--bad';
    if (severity === 'WARN') return 'event-dot--warn';
    return 'event-dot--info';
  }

  kindIcon(event: ChannelEvent): string {
    const map: Record<string, string> = {
      STATUS_CHANGE: 'mdi-swap-horizontal',
      QUALITY_CHANGE: 'mdi-chart-line',
      LIMIT_CHANGE: 'mdi-speedometer',
      ACCOUNT_UPDATE: 'mdi-account-alert-outline',
      ACCOUNT_REVIEW: 'mdi-clipboard-check-outline',
      TEMPLATE_QUALITY: 'mdi-file-document-alert-outline',
      BILLING: 'mdi-credit-card-off-outline',
      OPT_OUT: 'mdi-bell-off-outline',
      ACCESS_LOST: 'mdi-key-remove',
      NAME_UPDATE: 'mdi-rename-box',
    };
    return map[(event.kind ?? '').toUpperCase()] ?? 'mdi-information-outline';
  }
}
